import { ReadonlyURLSearchParams } from 'next/navigation';

export const getAuthRedirectURLFromSearchParams = (
    queryParams: URLSearchParams | ReadonlyURLSearchParams,
    fallbackURL: string = '/platform'
) => {
    // See if theres a `next` query parameter
    const nextParam = queryParams.get('next');

    return getAuthRedirectURL(nextParam, fallbackURL);
};

export const withNextParam = (url: string, next: string | undefined | null) => {
    if (next) {
        try {
            // Use a dummy base to support SSR (no window.location dependency)
            const urlObj = new URL(url, 'http://localhost');
            urlObj.searchParams.set('next', next);
            return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
        } catch {
            // If URL construction fails, fall back to the original URL
            return url;
        }
    }
    return url;
};

export const getAuthRedirectURL = (next: string | undefined | null, fallbackURL: string = '/platform') => {
    if (next) {
        // Make sure its safe and return when it is
        const { safe, final } = validateAuthRedirect(next);

        if (safe) {
            return final;
        }
    }

    // Final fallback, redirect to /platform
    return fallbackURL;
};

/**
 * Validate redirect URL similar to Django's url_has_allowed_host_and_scheme
 * Prevents open redirect vulnerabilities by ensuring URLs are either:
 * 1. Relative paths (starting with / but not //)
 * 2. Absolute URLs matching the BASE_URL domain (if configured)
 */
export const validateAuthRedirect = (url: string): { safe: boolean; final: string } => {
    // Empty or null URL
    if (!url || url.trim() === '') {
        return { safe: false, final: '' };
    }

    const trimmedUrl = url.trim();

    // Prevent protocol-relative URLs (//example.com)
    // These can redirect to external domains
    if (trimmedUrl.startsWith('//')) {
        return { safe: false, final: '' };
    }

    // Prevent URLs with newlines or other control characters
    if (/[\r\n\t]/.test(trimmedUrl)) {
        return { safe: false, final: '' };
    }

    // Allow relative paths (starting with / but not //)
    if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
        return { safe: true, final: trimmedUrl };
    }

    // For absolute URLs, validate against BASE_URL if available
    try {
        const parsedUrl = new URL(trimmedUrl);
        const baseUrl = process.env.BASE_URL;

        // Only allow http and https schemes
        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
            return { safe: false, final: '' };
        }

        // If BASE_URL is configured, validate against it
        if (baseUrl) {
            try {
                const parsedBaseUrl = new URL(baseUrl);
                const allowedHost = parsedBaseUrl.hostname;

                // Check if the URL host matches the allowed host
                // Support both exact match and subdomain match
                const urlHost = parsedUrl.hostname;

                const isAllowed = urlHost === allowedHost || urlHost.endsWith(`.${allowedHost}`);

                if (isAllowed) {
                    // Return the full URL including query params and hash
                    return { safe: true, final: trimmedUrl };
                }
            } catch {
                // BASE_URL parsing failed, reject the redirect
                return { safe: false, final: '' };
            }
        }

        // BASE_URL not configured - reject absolute URLs in production
        // This is safer than allowing all absolute URLs
        return { safe: false, final: '' };
    } catch {
        // URL parsing failed - likely a malformed URL
        return { safe: false, final: '' };
    }
};
